import React, { ReactNode, useEffect, useState } from 'react';
import SimpleModal from 'ui/Modals/SimpleModal';
import Button from '@mui/material/Button';
import { Breakpoint, PaperProps as PaperPropsType } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Tooltip from '@mui/material/Tooltip';

type ModalAction = {
    enabled: boolean;
    label: string;
    onClick?: () => void;
    shouldTriggerDefaultOnClose: boolean;
    tooltipMessage?: string;
    className?: string;
};

export type ModalStepT = {
    node: ReactNode;
    confirmAction?: Partial<ModalAction>;
    stepBackAction?: Partial<ModalAction>;
    leftActionGroup?: ReactNode;
    disableClose?: boolean;
    autoClose?: boolean;
    title?: ReactNode;
};

type Props = {
    isOpen?: boolean;
    onClose: () => void;
    maxWidth: Breakpoint;
    isSmallHeight?: boolean;
    showCloseIcon?: boolean;
    steps: ModalStepT[];
    PaperProps?: Partial<PaperPropsType>;
    className?: string;
    shouldRunStepBackOnReset?: boolean;
};

const defaultConfirm: Omit<ModalAction, 'onClick'> = {
    enabled: true,
    label: 'common.next',
    shouldTriggerDefaultOnClose: true,
};

const defaultCancel: Omit<ModalAction, 'onClick'> = {
    enabled: true,
    label: 'common.previous',
    shouldTriggerDefaultOnClose: true,
};

const MultiStepModal = ({
    isOpen = undefined,
    onClose,
    maxWidth,
    isSmallHeight = undefined,
    showCloseIcon = false,
    steps,
    PaperProps = undefined,
    className = undefined,
    shouldRunStepBackOnReset = true,
}: Props): React.ReactElement => {
    const [stepIndex, setStepIndex] = useState(0);
    const { t } = useTranslation();

    const resetStepIndex = () => {
        // TODO: refacto completely this logic
        // Running stepBackAction on reset can lead to weird behavior
        // where we reset some state before triggering a REST API call
        // This hacky bool has been added to prevent this behavior on NewInscriptionModal
        if (shouldRunStepBackOnReset) {
            const stepsToReset = steps.slice(0, stepIndex).reverse();
            stepsToReset.forEach((s) => {
                if (s.stepBackAction?.onClick !== undefined)
                    s.stepBackAction.onClick();
            });
        }
        setStepIndex(0);
    };

    useEffect(() => {
        if (!isOpen) {
            resetStepIndex();
        }
    }, [isOpen]);

    const {
        node,
        confirmAction,
        stepBackAction,
        autoClose,
        leftActionGroup,
        disableClose = false,
        title,
    } = steps[stepIndex];

    const mergedConfirmAction: ModalAction = {
        ...defaultConfirm,
        ...confirmAction,
    };
    const mergedStepBackAction: ModalAction = {
        ...defaultCancel,
        ...(stepIndex === 0 ? { label: 'common.close' } : {}),
        ...stepBackAction,
    };

    useEffect(() => {
        // Automatically triggers onClose() after 1500ms if conditions are met,
        // but clears the timeout if isOpen change before execution.
        let timeoutId: NodeJS.Timeout | undefined;

        if (autoClose && mergedConfirmAction.enabled && isOpen) {
            timeoutId = setTimeout(() => {
                onClose();
            }, 1500);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [isOpen, autoClose, mergedConfirmAction.enabled]);

    const onStepCompleted = () => {
        if (mergedConfirmAction.onClick !== undefined)
            mergedConfirmAction.onClick();

        if (stepIndex + 1 < steps.length) {
            setStepIndex(stepIndex + 1);
        } else if (stepIndex + 1 === steps.length) {
            if (mergedConfirmAction.shouldTriggerDefaultOnClose) onClose();
            else resetStepIndex();
        }
    };

    const onStepCanceled = () => {
        if (mergedStepBackAction.onClick !== undefined)
            mergedStepBackAction.onClick();

        if (stepIndex === 0) {
            if (mergedStepBackAction.shouldTriggerDefaultOnClose) onClose();
            else resetStepIndex();
        } else {
            setStepIndex(stepIndex - 1);
        }
    };

    const cancelStepButton = !autoClose && (
        <Tooltip
            title={mergedStepBackAction.tooltipMessage}
            key="cancel-button"
        >
            <span>
                <Button
                    data-testid="e2e-multi-step-modal-cancel-button"
                    disabled={!mergedStepBackAction.enabled}
                    onClick={onStepCanceled}
                >
                    {t(mergedStepBackAction.label)}
                </Button>
            </span>
        </Tooltip>
    );

    const completeStepButton = !autoClose && (
        <Tooltip
            title={mergedConfirmAction.tooltipMessage}
            key="complete-button"
        >
            <span>
                <Button
                    data-testid="e2e-multi-step-modal-complete-button"
                    disabled={!mergedConfirmAction.enabled}
                    onClick={onStepCompleted}
                    variant="contained"
                    className={mergedConfirmAction.className}
                >
                    {t(mergedConfirmAction.label)}
                </Button>
            </span>
        </Tooltip>
    );

    return (
        <SimpleModal
            open={isOpen}
            onClose={onClose}
            maxWidth={maxWidth}
            isSmallHeight={isSmallHeight}
            additionalButtons={[cancelStepButton, completeStepButton]}
            leftActionGroup={leftActionGroup}
            showCloseButton={false}
            disableClosing={disableClose}
            dialogTitle={title}
            PaperProps={PaperProps}
            className={className}
            showCloseIcon={showCloseIcon}
        >
            {node}
        </SimpleModal>
    );
};

export default MultiStepModal;
